import { Box, Button, Grid } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../../../lib/api/auth/useAuth';
import {
  CategoryQueryKeys,
  getAllCategories,
} from '../../../lib/api/categories/categories.service';
import CategoryProductList from '../expandable-rows/CategoryProductList';
import StoreCategoryRow from '../expandable-rows/StoreCategoryRow';
import CartSummary from '../../../components/cart-summary/CartSummary';
import ShoppingForWeekPicker from './ShoppingForWeekPicker';
import { useCartContext } from '../../../contexts/CartContext';
import { downloadStoreAvailability } from '../../../lib/common/report-download.service';
import FullScreenModal from '../../../components/ui/modal/FullScreenModal';
import useModal from '../../../components/ui/modal/useModal';
import ReactMarkdown from 'react-markdown';
import UpdateStoreFeatureNoteForm from '../../../components/store/UpdateStoreFeatureNoteForm';
import EditIconButton from '../../../components/ui/buttons/EditIconButton';

export default function StorePageInternal() {
  const { authed, loggedInUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const underAdminRoute =
    location.pathname.split('/').findIndex((part) => part === 'admin') !== -1;
  const { storeStatus } = useCartContext();

  const {
    isError: categoriesIsError,
    data: categoriesRes,
    error: categoriesError,
    isFetching: categoriesIsFetching,
  } = useQuery([CategoryQueryKeys.findAllCategories], () => getAllCategories());

  const {
    handleCloseModal: handleCloseEditFeatureNoteModal,
    handleLaunchModal: handleLaunchEditFeatureNoteModal,
    modalOpen: editFeatureNoteModalOpen,
  } = useModal();

  if (!storeStatus) return null;
  if (!authed()) return <Navigate to="/auth/login" />;

  const handleEditFeatureNote = () => {
    handleLaunchEditFeatureNoteModal();
  };

  const onEditFeatureNoteSuccess = () => {
    handleCloseEditFeatureNoteModal();
  };

  return (
    <>
      <Grid container spacing={4}>
        {loggedInUser.isAdmin && (
          <>
            <FullScreenModal
              title={`Feature Note`}
              open={editFeatureNoteModalOpen}
              onClose={handleCloseEditFeatureNoteModal}
            >
              <UpdateStoreFeatureNoteForm
                storeStatus={storeStatus}
                onSuccess={onEditFeatureNoteSuccess}
              />
            </FullScreenModal>
            <FullScreenModal
              title={`Feature Note`}
              open={editFeatureNoteModalOpen}
              onClose={handleCloseEditFeatureNoteModal}
            >
              <UpdateStoreFeatureNoteForm
                storeStatus={storeStatus}
                onSuccess={onEditFeatureNoteSuccess}
              />
            </FullScreenModal>
            {!storeStatus.featureNote && (
              <Grid
                item
                sm={12}
                md={7}
                sx={{ position: 'sticky', top: '70px', zIndex: 1000 }}
              >
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: '150px' }}
                    onClick={handleEditFeatureNote}
                  >
                    Add Store Featured Note
                  </Button>
                </>
              </Grid>
            )}
            {storeStatus.featureNote && (
              <Grid
                item
                sm={12}
                md={7}
                sx={{ position: 'sticky', top: '70px', zIndex: 1000 }}
              >
                <Box
                  sx={{
                    backgroundColor: 'grey.200',
                    borderRadius: 1,
                    padding: 1,
                    marginBottom: 2,
                    '& p:first-of-type': { marginTop: 0 },
                    '& p:last-of-type': { marginBottom: 0 },
                  }}
                >
                  <Box sx={{ float: 'right' }}>
                    <EditIconButton
                      onClick={handleEditFeatureNote}
                      resource={'Feature Note'}
                      size="small"
                    />
                  </Box>

                  <ReactMarkdown
                    components={{
                      a: ({ href, children, ...props }) => (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          {...props}
                        >
                          {children}
                        </a>
                      ),
                    }}
                  >
                    {storeStatus.featureNote}
                  </ReactMarkdown>
                </Box>
              </Grid>
            )}
          </>
        )}

        <Grid item sm={12} md={7}>
          <ShoppingForWeekPicker />
          <Button
            variant="outlined"
            sx={{ mt: 2, mb: 2 }}
            onClick={downloadStoreAvailability}
          >
            Download Availability
          </Button>
          {categoriesRes?.data?.map((category) => (
            <StoreCategoryRow
              key={`category-${category.id}`}
              category={category}
              expandedComponent={<CategoryProductList category={category} />}
            />
          ))}
        </Grid>
        <Grid
          item
          sm={12}
          md={5}
          sx={{
            height: '100%',
            // For screens larger than 900px, use fixed positioning
            '@media (min-width: 900px)': {
              position: 'fixed',
              top: '70px',
              right: '0',
              height: 'calc(100vh + 50px)',
              overflowY: 'scroll',
            },
          }}
        >
          <CartSummary
            onCheckout={() =>
              navigate(underAdminRoute ? '/admin/cart' : '/cart')
            }
          />
        </Grid>
      </Grid>
    </>
  );
}
